import React, {useEffect, useRef} from "react";
import {objToArrK} from "../tools/CommonJS";
import {TextInput, useInputChange} from "../tools/Inputs";
import {FilterList} from "../tools/UIControls";
import {UIComboBox} from "../tools/UIComboBox";
import {appCtx} from "../app/AppContext";
import {checkModuleLicense, useLicense} from "../app/UseLicense";
import {InputGroup} from "../app/UseAppData";
import {FormHint} from "../app/CommonUIPopups";
import {SidePanelContainer} from "../app/UseListSelectable";
import {Divider, FilterEntry} from "../app/CommonUIComponenets";
import {UIDelayedCombiField, useCombiFields} from "../app/UseCombiFields";
import {doesSearchIncTpc} from "../app/CommonUIData";
import {HA_topics} from "../app/MisconData";
import {Benchmark_cisProfiles, Benchmark_rules, cisLevelDefs, cisLevelOpts} from "../app/BenchmarkData";

export function SearchGlobalPanel({onClose}){
    const {inp, onChange, setInp} = useInputChange({searchType: "device", level: "Level 1"})
    const vals = useRef({}).current;
    const {curLic} = useLicense();
    const searchRef = useRef();
    vals.inp = inp;

    const genQueryParams = () => ({
        os: vals.inp.os,
        groupId: vals.inp.groupId,
        device: vals.inp.device,
        ip: vals.inp.ip
    });

    const {combis, onChangeQuery} = useCombiFields({
        api: "/api/v2/general_combi-field",
        genQueryParams,
        setInp,
        inp,
        vals,
        cacheMins: false,
        combis: [{k: "device", h: "Device Name"}, {k: "ip", h: "IP Address"}]
    });
    const [deviceComb, ipComb] = combis;
    const optsByField = {
        device: () => deviceComb.rsp?.data,
        ip: () => ipComb.rsp?.data,
        misconTopic: () => Object.entries(HA_topics).filter(([_, tpc]) =>
            doesSearchIncTpc(tpc, inp.misconTopic || "") &&
            (!inp.cvss ||  tpc.cvss && String(tpc.cvss).startsWith(inp.cvss))).map(([k, v]) =>
            ({h: v?.title + (v.cvss ? ` (CVSS: ${v.cvss})` : ""), k: k})),
        benchmark: () => Object.entries(Benchmark_rules || {}).filter(([_, tpc]) =>
            doesSearchIncTpc(tpc, inp.benchmark || "") && tpc.profiles.some(profile => Benchmark_cisProfiles[profile]?.level === inp.level)).map(([code, tpc]) => ({h: tpc.title, k: code})),
    }

    function onSelectSearch(f, v, i) {
        onChange(f, v);
        if (inp.searchType === "misconTopic") {
            appCtx.nav.to("/miscon_overview/topic",
                {topicCode: v, selTarget: inp.os, tObj: [HA_topics[v]?.title]},
                {isReset: true});
        } else if (inp.searchType === "device") {
            appCtx.nav.to("/device_info",
                {...(f === "device" ? {device: v} : {ip: v}), searchBy: f, deviceId: (f === "device" ? deviceComb : ipComb).rsp?.deviceIds[i], tObj:[`Device Info: ${v}`]},
                {isReset: true});
        } else if (inp.searchType === "benchmark") {
            appCtx.nav.to("/benchmark/CIS/issues/topic",
                {topicCode: v, benchmarkOrg: "CIS", benchmarkProfile: cisLevelDefs[inp.level]?.comb, tObj: [Benchmark_rules[v]?.title]},
                {isReset: true});
        }
        onClose();
    }

    useEffect(() => {
        searchRef?.current?.focus()
    }, [searchRef]);

    return <SidePanelContainer {...{onClose}} title="Global Search">
        <div className="flex flex-col gap-8 p-4">
            <FilterEntry title="What are you looking for?">
                <UIComboBox selectMode onChange={(v, f) => (onChange(v, f), onChange("search", ""))}
                            field="searchType" value={inp.searchType} opts={searchTypeOpts.filter(opt => !opt.perms || checkModuleLicense(curLic, opt.perms))}/>
            </FilterEntry>
            {inp.searchType === "device" && <FilterEntry title="Filter by Device Group">
                <InputGroup groupId={inp.groupId} onChange={onChangeQuery}/>
            </FilterEntry>}
            {inp.searchType === "misconTopic" && <FilterEntry title="CVSS" info={<FormHint tip="the Common Vulnerability Scoring System, when 10 is the highest score"/>}>
                <TextInput max="10" min="1" type="number" field="cvss" value={inp.cvss} className="w-fit" onChange={onChange} placeholder="cvs score..." />
            </FilterEntry>}
            <Divider/>
            <FilterEntry title={searchTypeDefs[inp.searchType]?.h}>
                {inp.searchType === "device" ?
                    <UIDelayedCombiField
                        inputRef={searchRef} combi={deviceComb}
                        field={inp.searchType} value={inp[inp.searchType]}
                        placeholder={searchTypeDefs[inp.searchType]?.search + "..."}
                        opts={optsByField[inp.searchType]()} onChange={onChangeQuery} onSelect={onSelectSearch}/>
                    : <UIComboBox inputRef={searchRef} field={inp.searchType} value={inp[inp.searchType]}
                                  placeholder={searchTypeDefs[inp.searchType]?.search + "..."} classNameWrapper="w-full"
                                  className="h-[3.75rem] text-lg focus:ring-prm-dark focus:shadow-[0_0_10px_0_#4897FF] w-full focus:ring-2 outline-none"
                                  classNameListItem={"border-y border-divider py-2 bg-bg2 rounded"}
                                  opts={optsByField[inp.searchType]()} onChange={onChangeQuery} onSelect={onSelectSearch}/>}
            </FilterEntry>
            {inp.searchType === "device" && <FilterEntry title="Search by IP address">
                <UIDelayedCombiField
                    value={inp.ip} combi={ipComb} opts={optsByField.ip()} field="ip"
                    placeholder={"IP address..."} onChange={onChangeQuery} onSelect={onSelectSearch}
                />
            </FilterEntry>}
            {inp.searchType === "benchmark" && <FilterEntry title="Compliance Profile">
                <FilterList w="w-80 h-9" field="level" opts={cisLevelOpts} value={inp.level} onChange={onChange}/>
            </FilterEntry>}
        </div>
    </SidePanelContainer>;
}

const searchTypeDefs = {
    device: {h: "Device name", search: "Device name"},
    misconTopic: {h: "Miscon. topic", search: "Topic name"},
    benchmark: {h: "CIS Standard", search: "Security standard rule", perms: "benchmark"},
}, searchTypeOpts = objToArrK(searchTypeDefs);
